import { GetAdditionalMetadataType } from '@informatica/droplets-analytics';

export type GetLicenseInfoResponse = {
	active: License[];
	expired: License[];
	gracePeriod: License[];
};
export type License = {
	expirationDate: Date;
	id: string;
	licenseType: 'FREE' | 'SUBSCRIPTION' | 'TRIAL';
	name: string;
};
const dataLoaderLicenseDetails = {
	dataLoaderFreemium: {
		id: '6Ssh7N7rII5cwfrbFFg96b',
		activeLabel: 'Data Loader',
	},
	simpleDiPaygo: {
		id: '3PvSr7NEU77gmc5BT6rE0A',
		activeLabel: 'CDI-Free',
		expiredLabel: 'CDI-Free: Expired',
	},
	simpleDiPaygoSubscription: {
		id: 'agLCxDGxfAadQnyGSIF5cB',
		activeLabel: 'CDI-PayGo',
		expiredLabel: 'CDI-PayGo: Expired',
	},
};

const licenseManager = (licenseInfo: GetLicenseInfoResponse) => {
	const activeLicenseMap: Map<string, string> = new Map();
	const expiredLicenseMap: Map<string, string> = new Map();
	const gracePeriodLicenseMap: Map<string, string> = new Map();

	licenseInfo.active.forEach((license: License) => {
		activeLicenseMap.set(license.id, license.id);
	});
	licenseInfo.expired.forEach((license: License) => {
		activeLicenseMap.set(license.id, license.id);
	});
	licenseInfo.gracePeriod.forEach((license: License) => {
		activeLicenseMap.set(license.id, license.id);
	});

	return {
		activeLicenseMap,
		expiredLicenseMap,
		gracePeriodLicenseMap,
	};
};

const isLicenseExpriedOrInGracePeriod = (licenseId: string, expiredLicenseMap: Map<string, string>, gracePeriodLicenseMap: Map<string, string>) =>
	expiredLicenseMap.has(licenseId) || gracePeriodLicenseMap.has(licenseId);

const is1BPlan = (activeLicenseMap: Map<string, string>, expiredLicenseMap: Map<string, string>, gracePeriodLicenseMap: Map<string, string>) =>
	activeLicenseMap.has(dataLoaderLicenseDetails.dataLoaderFreemium.id) ||
	activeLicenseMap.has(dataLoaderLicenseDetails.simpleDiPaygo.id) ||
	isLicenseExpriedOrInGracePeriod(dataLoaderLicenseDetails.simpleDiPaygo.id, expiredLicenseMap, gracePeriodLicenseMap);

const determine1BPlanLevel = (
	activeLicenseMap: Map<string, string>,
	expiredLicenseMap: Map<string, string>,
	gracePeriodLicenseMap: Map<string, string>,
): string => {
	if (isLicenseExpriedOrInGracePeriod(dataLoaderLicenseDetails.simpleDiPaygoSubscription.id, expiredLicenseMap, gracePeriodLicenseMap)) {
		return dataLoaderLicenseDetails.simpleDiPaygoSubscription.expiredLabel;
	} else if (isLicenseExpriedOrInGracePeriod(dataLoaderLicenseDetails.simpleDiPaygo.id, expiredLicenseMap, gracePeriodLicenseMap)) {
		return dataLoaderLicenseDetails.simpleDiPaygo.expiredLabel;
	} else {
		// loop will work as we expect only one of the license to be present for a user
		Object.values(dataLoaderLicenseDetails).forEach(({ id, activeLabel }, index) => {
			if (activeLicenseMap.has(id)) {
				return activeLabel;
			}
		});
	}
	return 'UNKNOWN';
};

const getLicensePlanLevel = (licenseInfo: GetLicenseInfoResponse): Promise<string> =>
	new Promise((resolve) => {
		let planLevel = '1A';

		const { activeLicenseMap, expiredLicenseMap, gracePeriodLicenseMap } = licenseManager(licenseInfo);
		if (is1BPlan(activeLicenseMap, expiredLicenseMap, gracePeriodLicenseMap)) {
			planLevel = determine1BPlanLevel(activeLicenseMap, expiredLicenseMap, gracePeriodLicenseMap);
		}

		resolve(planLevel);
	});

export const getAdditionalAccountMetadataCallback = async (licenseInfo: GetLicenseInfoResponse): Promise<GetAdditionalMetadataType> => {
	const planLevel = await getLicensePlanLevel(licenseInfo);
	return () => {
		const metadata = new Map<string, string | string[] | boolean>();
		metadata.set('planLevel', planLevel);
		return metadata;
	};
};
